/**
 *
 * Page header with icon
 *
 */

import React, { FC, ReactNode } from 'react';

import { Box, BoxProps, HStack } from '@chakra-ui/react';
import { colorOpacity, customTheme, HeadingFontFamily } from 'theme';

import { AriksaIcon } from 'components/DataDisplay';
import { IconSize, IconType } from 'components/Icons';
import { getIconColor } from 'components/Icons/Components/getIconColor';

export interface PageHeaderWithIconProps {
  label: string | ReactNode;
  icon?: ReactNode;
  iconBgColor?: string;
  iconType?: IconType;
  iconFilled?: boolean;
  reversed?: boolean;
  useCustomColor?: boolean;
  fontSize?: any;
  fontWeight?: any;
  fontColor?: string;
  iconColor?: string;
  spacing?: any;
  iconSize?: IconSize;
  labelWidth?: string | number;
  border?: boolean;
  borderColor?: string;
  onClickIcon?();
  labelStyles?: any;
  iconStyles?: BoxProps;
}

export const PageHeaderWithIcon: FC<PageHeaderWithIconProps> = props => {
  const {
    iconBgColor,
    label,
    reversed = true,
    icon,
    iconType,
    fontSize = '22px',
    spacing = 4,
    iconSize = 'md',
    labelWidth,
    fontColor,
    iconColor,
    onClickIcon,
    labelStyles,
    iconStyles,
    fontWeight = 400,
    ...rest
  } = props;
  const iconColorValue =
    iconColor ??
    (iconType ? getIconColor(iconType as string) : undefined) ??
    customTheme.colors.primary;
  const resolvedIconColor =
    iconColorValue === 'primary' ? customTheme.colors.primary : iconColorValue;
  const iconBackgroundColor =
    iconBgColor ?? colorOpacity(resolvedIconColor, 0.2);

  const renderIcon = () => (
    <AriksaIcon
      bg={iconBackgroundColor}
      size={iconSize}
      icon={icon}
      type={iconType}
      color={resolvedIconColor}
      onClick={onClickIcon}
      useCustomColor
      style={{ ...iconStyles, borderRadius: 12 }}
      {...rest}
    />
  );

  const renderLabel = () => (
    <Box
      fontSize={fontSize}
      w={labelWidth}
      color={fontColor}
      overflow="hidden"
      textOverflow="ellipsis"
      whiteSpace="nowrap"
      fontWeight={fontWeight}
      fontFamily={HeadingFontFamily}
      {...labelStyles}
    >
      {label}
    </Box>
  );

  return (
    <HStack h="full" align="center" spacing={spacing}>
      {reversed ? (
        <>
          {(icon || iconType) && renderIcon()}
          {renderLabel()}
        </>
      ) : (
        <>
          {renderLabel()}
          {(icon || iconType) && (
            <Box borderRight="1px solid" borderColor="gray.600" h="20px" />
          )}
          {(icon || iconType) && renderIcon()}
        </>
      )}
    </HStack>
  );
};
